import React, { useEffect, useState, useMemo } from "react";
// import { useThemeHook } from "../GlobalComponents/ThemeProvider";
import { BiSearch } from "react-icons/bi";
import SearchFilter from "react-filter-search";
import ProductCard from "./ProductCard";
import { db } from "../firebaseConfig";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import {
  saveListContext,
  useShopScrollRestore,
} from "../utils/shopScrollRestore";

// Define categories with descriptions
const categories = [
  {
    name: "Fragrance",
    description: "Elevate your presence with timeless fragrances that speak elegance, confidence, and unforgettable moments."
  },
  {
    name: "Throw Covers",
    description: "Add comfort and character to your space with beautifully designed throw covers and pillows that elevate any room."
  },
  {
    name: "Frames",
    description: "Elevate your walls with beautifully crafted frames that turn your space into a statement."
  },
  {
    name: "Furniture",
    description: "Where comfort meets design—find furniture that defines your space."
  },
  {
    name: "Wall Art",
    description: "Transform your walls with expressive wall art that adds beauty, character, and style."
  },
  {
    name: "Decor Accessories",
    description: "Discover decorative accents designed to enhance your interiors with style and personality."
  },
  {
    name: "Mats",
    description: "Durable and stylish mats designed to keep your space clean, comfortable, and inviting."
  },
  {
    name: "Clocks",
    description: "Stylish clocks designed to keep time beautifully while enhancing your décor."
  },
  {
    name: "Decorative Mirrors",
    description: "Enhance your space with beautifully designed mirrors that add depth, light, and elegance."
  },
  {
    name: "Gift Items",
    description: "Thoughtfully selected gift items perfect for celebrating special moments and meaningful connections."
  },
  {
    name: "Household Items",
    description: "Quality household items designed for comfort, efficiency, and a modern home."
  },
  {
    name: "Flowers/Pots",
    description: "Nature meets design—flowers and pots that transform your space instantly."
  }
];

const Homes = ({title}) => {
  // const [theme] = useThemeHook();
  const [searchInput, setSearchInput] = useState("");
  const [productData, setProductData] = useState([]);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const selectedCategory = searchParams.get("category");

  useEffect(() => {
    saveListContext({
      searchInput,
      pathname: location.pathname,
      search: location.search,
    });
  }, [searchInput, location.pathname, location.search]);

  useShopScrollRestore({
    isReady: productData.length > 0,
    restoreDeps: [searchInput],
    onRestore: (state) => {
      if (state.searchInput) {
        setSearchInput(state.searchInput);
      }
    },
  });

  useEffect(() => {
    async function getResponse() {
      const collection = db.collection("Products")
      const snapshot = await collection.get();
      const Products = [];

      snapshot.forEach((doc) => {
        Products.push({
          id: doc.id,
          ...doc.data(),
        })
      });

      setProductData(Products);
    }

    getResponse();
  }, []);

  // Group products by category
  const productsByCategory = useMemo(() => {
    const grouped = {};
    
    categories.forEach(category => {
      grouped[category.name] = productData.filter(product => 
        product.category === category.name || product.Cat === category.name
      );
    });

    return grouped;
  }, [productData]);

  // Filter products based on search
  const filteredProductsByCategory = useMemo(() => {
    if (!searchInput.trim()) {
      return productsByCategory;
    }

    const searchLower = searchInput.toLowerCase();
    const filtered = {};

    categories.forEach(category => {
      const categoryProducts = productsByCategory[category.name] || [];
      filtered[category.name] = categoryProducts.filter(product => {
        const name = (product.product_name || product.title || "").toLowerCase();
        const categoryName = (product.category || product.Cat || "").toLowerCase();
        return name.includes(searchLower) || categoryName.includes(searchLower);
      });
    });

    return filtered;
  }, [searchInput, productsByCategory]);

  // Filter products for selected category
  const categoryProducts = useMemo(() => {
    if (!selectedCategory) return [];
    return productData.filter(product => 
      product.category === selectedCategory || product.Cat === selectedCategory
    );
  }, [productData, selectedCategory]);

  // Check if we're in search mode or category view mode
  const isSearchMode = searchInput.trim().length > 0;
  const isCategoryView = selectedCategory && !isSearchMode;

  return (
    <div className="h-[100%] w-[100%] pb-20"       
    >
      <div className={`text-center ${title === 'me' ? "pt-0" : "pt-20"} `}>
        <h1 className=" pt-10 text-5xl font-bold dark:text-white">
       Shop
        </h1>
        {isCategoryView && (() => {
          const category = categories.find(cat => cat.name === selectedCategory);
          return (
            <div className="flex flex-col items-center justify-center gap-4 mb-4">
              <div className="flex items-center justify-center gap-4">
                <Link
                  to="/shop"
                  className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  ← Back to All Categories
                </Link>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {selectedCategory}
                </h2>
              </div>
              {category && (
                <p className="text-gray-600 dark:text-gray-400 text-center max-w-2xl px-4">
                  {category.description}
                </p>
              )}
            </div>
          );
        })()}
        <div className="relative inline-block">
          <BiSearch
            size="16"
            className="absolute left-8 top-1/2 transform -translate-y-1/2 z-0"
            color="gray"
          />
          <input
            placeholder="Search products, brands, & categories."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="text-[#555] bg-[#eee] h-10 lg:w-96 w-[300px] rounded-md m-5 pl-10 pr-4 outline-none text-sm"
          />
        </div>
      </div>

      {isSearchMode ? (
        // Search mode: show all filtered results
        <SearchFilter
          value={searchInput}
          data={productData}
          renderResults={(results) => (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-4">
              {results.map((item, i) => (
                <ProductCard data={item} key={i} />
              ))}
            </div>
          )}
        />
      ) : isCategoryView ? (
        // Category view mode: show all products for selected category
        <div className="px-4">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {categoryProducts.map((item, i) => (
              <ProductCard data={item} key={item.id || i} />
            ))}
          </div>
          {categoryProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                No products found in this category.
              </p>
            </div>
          )}
        </div>
      ) : (
        // Category mode: show products organized by category
        <div className="px-4 space-y-12">
          {categories.map((category) => {
            const categoryProducts = filteredProductsByCategory[category.name] || [];
            const displayProducts = categoryProducts.slice(0, 4);
            const hasMore = categoryProducts.length > 4;

            if (displayProducts.length === 0) return null;

            return (
              <div key={category.name} className="space-y-6">
                {/* Category Header */}
                <div className="flex flex-col items-center justify-center px-2">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center">
                    {category.name}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-center mt-2 max-w-2xl px-4 text-sm">
                    {category.description}
                  </p>
                  {hasMore && (
                    <Link
                      to={`/shop?category=${encodeURIComponent(category.name)}`}
                      className="mt-2 text-orange-500 hover:text-orange-600 font-semibold text-sm flex items-center gap-1 transition-colors"
                    >
                      See More
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  )}
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {displayProducts.map((item, i) => (
                    <ProductCard data={item} key={item.id || i} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Homes;
